import type { IAccount, IAccountToken, ITransaction } from 'oscar-typedef'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { Maybe } from '@/types'
import { getExecuteOnce } from '@/utils'

const useTransactionAPIStore = defineStore('transactionAPI', () => {
  const transactionListByAccountId = ref<Record<string, Maybe<ITransaction[]>>>(
    {},
  )
  const transactionListByAccountTokenId = ref<
    Record<string, Record<string, Maybe<ITransaction[]>>>
  >({})

  type PageAndAllLoading = {
    allLoading: boolean
    page: number
  }
  interface PageByAccountId {
    [accountId: string]: PageAndAllLoading
  }

  const pageByAccountId = ref<PageByAccountId>({})
  const pageByAccountTokenId = ref<PageByAccountId>({})

  async function checkAddressValidation(coinId: string, address: string) {
    const response =
      await WepinBackendAPI.getInstance().transaction.checkAddressValidation({
        coinId,
        address,
      })
    return response
  }

  const setPageAndAllLoading = (
    pageByAccountId: PageByAccountId,
    length: number,
    accountId: string,
  ) => {
    const { page } = pageByAccountId[accountId]
    pageByAccountId[accountId] = {
      page: page + 1,
      allLoading: length < 15 ? true : false,
    }
  }

  /**
   * 펜딩 트랜잭션 상태 확인 및 업데이트
   */
  const checkPendingTransactions = async (accountId: string) => {
    const transactions = transactionListByAccountId.value[accountId]
    if (!transactions) return

    // 펜딩 상태인 트랜잭션 필터링
    const pendingTxs = transactions.filter((tx) => tx.status === 'pending')
    if (pendingTxs.length === 0) return

    try {
      // 펜딩 트랜잭션의 id로 receipt 조회 (txHash가 아닌 id 사용)
      const txIds = pendingTxs.map((tx) => tx.id).filter(Boolean)
      if (txIds.length === 0) return

      const receiptResponse =
        await WepinBackendAPI.getInstance().transaction.getTransactionReceipt({
          txIds,
        })

      // 응답에서 트랜잭션 상태 업데이트
      const receipts = receiptResponse?.transaction || []
      if (receipts.length === 0) return

      // 트랜잭션 상태 업데이트
      transactionListByAccountId.value[accountId] = transactions.map((tx) => {
        const receipt = receipts.find((r: any) => r.id === tx.id)
        if (receipt) {
          const newStatus =
            receipt.status?.toLowerCase() === 'confirmed'
              ? 'confirm'
              : receipt.status?.toLowerCase()
          if (newStatus !== tx.status) {
            return {
              ...tx,
              status: newStatus,
            }
          }
        }
        return tx
      })
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * 토큰 펜딩 트랜잭션 상태 확인 및 업데이트
   */
  const checkPendingTokenTransactions = async (
    accountId: string,
    accountTokenId: string,
  ) => {
    const transactions =
      transactionListByAccountTokenId.value[accountId]?.[accountTokenId]
    if (!transactions) return

    // 펜딩 상태인 트랜잭션 필터링
    const pendingTxs = transactions.filter((tx) => tx.status === 'pending')
    if (pendingTxs.length === 0) return

    try {
      // 펜딩 트랜잭션의 id로 receipt 조회
      const txIds = pendingTxs.map((tx) => tx.id).filter(Boolean)
      if (txIds.length === 0) return

      const receiptResponse =
        await WepinBackendAPI.getInstance().transaction.getTransactionReceipt({
          txIds,
        })

      // 응답에서 트랜잭션 상태 업데이트
      const receipts = receiptResponse?.transaction || []
      if (receipts.length === 0) return

      // 트랜잭션 상태 업데이트
      transactionListByAccountTokenId.value[accountId][accountTokenId] =
        transactions.map((tx) => {
          const receipt = receipts.find((r: any) => r.id === tx.id)
          if (receipt) {
            const newStatus =
              receipt.status?.toLowerCase() === 'confirmed'
                ? 'confirm'
                : receipt.status?.toLowerCase()
            if (newStatus !== tx.status) {
              return {
                ...tx,
                status: newStatus,
              }
            }
          }
          return tx
        })
    } catch (error) {
      // Error handled silently
    }
  }

  const fetchAccountTransactionList = getExecuteOnce(
    async ({
      accountId,
      force = false,
      refresh = false,
    }: {
      accountId: IAccount['id']
      force?: boolean
      refresh?: boolean
    }) => {
      if (!pageByAccountId.value[accountId] || refresh)
        pageByAccountId.value[accountId] = {
          page: 1,
          allLoading: false,
        }
      if (
        pageByAccountId.value[accountId].allLoading ||
        (!force && transactionListByAccountId.value[accountId])
      ) {
        return
      }
      if (!transactionListByAccountId.value[accountId])
        transactionListByAccountId.value[accountId] = []
      if (refresh) {
        const res =
          await WepinBackendAPI.getInstance().transaction.getAccountTransactionList(
            {
              accountId,
              page: pageByAccountId.value[accountId].page,
            },
          )
        transactionListByAccountId.value[accountId] = res.sort(
          (a: any, b: any) =>
            new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
        )
        setPageAndAllLoading(pageByAccountId.value, res.length, accountId)
      } else {
        const res =
          await WepinBackendAPI.getInstance().transaction.getAccountTransactionList(
            {
              accountId,
              page: pageByAccountId.value[accountId].page,
            },
          )
        const tempList =
          transactionListByAccountId.value[accountId]?.concat(res)
        tempList?.sort(
          (a, b) =>
            new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
        )
        transactionListByAccountId.value[accountId] = tempList
        setPageAndAllLoading(pageByAccountId.value, res.length, accountId)
      }

      // 펜딩 트랜잭션 상태 확인
      await checkPendingTransactions(accountId)
    },
  )
  const fetchAccountTokenTransactionList = getExecuteOnce(
    async ({
      accountId,
      accountTokenId,
      contract,
      force = false,
      refresh = false,
    }: {
      accountId: IAccount['id']
      accountTokenId: IAccountToken['id']
      contract: string
      force?: boolean
      refresh?: boolean
    }) => {
      if (!pageByAccountTokenId.value[accountTokenId] || refresh)
        pageByAccountTokenId.value[accountTokenId] = {
          page: 1,
          allLoading: false,
        }
      if (
        pageByAccountTokenId.value[accountTokenId].allLoading ||
        (!force &&
          transactionListByAccountTokenId.value[accountId] &&
          transactionListByAccountTokenId.value[accountId][accountTokenId])
      ) {
        return
      }

      if (!transactionListByAccountTokenId.value[accountId]) {
        transactionListByAccountTokenId.value[accountId] = {}
        transactionListByAccountTokenId.value[accountId][accountTokenId] = []
      }
      if (refresh) {
        const res =
          await WepinBackendAPI.getInstance().transaction.getAccountTransactionList(
            {
              accountId,
              accountTokenId,
              contract,
              page: pageByAccountTokenId.value[accountTokenId].page,
            },
          )
        transactionListByAccountTokenId.value[accountId][accountTokenId] =
          res.sort(
            (a: any, b: any) =>
              new Date(b.createDate).getTime() -
              new Date(a.createDate).getTime(),
          )
        setPageAndAllLoading(
          pageByAccountTokenId.value,
          res.length,
          accountTokenId,
        )
      } else {
        const res =
          await WepinBackendAPI.getInstance().transaction.getAccountTransactionList(
            {
              accountId,
              accountTokenId,
              contract,
              page: pageByAccountTokenId.value[accountTokenId].page,
            },
          )
        const tempList =
          transactionListByAccountTokenId.value[accountId][
            accountTokenId
          ]?.concat(res)
        tempList?.sort(
          (a, b) =>
            new Date(b.createDate).getTime() - new Date(a.createDate).getTime(),
        )
        transactionListByAccountTokenId.value[accountId][accountTokenId] =
          tempList
        setPageAndAllLoading(
          pageByAccountTokenId.value,
          res.length,
          accountTokenId,
        )
      }

      // 펜딩 토큰 트랜잭션 상태 확인
      await checkPendingTokenTransactions(accountId, accountTokenId)
    },
  )

  const reset = () => {
    transactionListByAccountId.value = {}
    transactionListByAccountTokenId.value = {}
    pageByAccountId.value = {}
    pageByAccountTokenId.value = {}
  }
  return {
    transactionListByAccountId,
    transactionListByAccountTokenId,
    checkAddressValidation,
    fetchAccountTransactionList,
    fetchAccountTokenTransactionList,
    checkPendingTransactions,
    checkPendingTokenTransactions,
    reset,
  }
})

export { useTransactionAPIStore }
